import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useI18n } from '@/providers/i18n';
import type { RelightParams } from './types';
import { Button } from '@/components/ui/button';
import { Upload as UploadIcon } from 'lucide-react';
import { useNodeId, useReactFlow } from '@xyflow/react';
import { nanoid } from 'nanoid';

export const RelightPanel = ({
  params,
  onChange,
}: {
  params: RelightParams;
  onChange: (patch: Partial<RelightParams>) => void;
}) => {
  const { t } = useI18n();
  const nodeId = useNodeId();
  const { addNodes, addEdges, getNode, setNodes } = useReactFlow();
  const hasImage =
    typeof (params as any)?.source_image_url === 'string' &&
    String((params as any).source_image_url).trim().length > 0;
  const uploadLabel = hasImage ? t('Click Run in toolbar') : t('Upload Image');

  const handleAddUpload = () => {
    if (!nodeId) return;
    if (hasImage) return;
    try {
      const toolNode = getNode(nodeId) as any;
      if (!toolNode) return;
      const baseX = (toolNode?.position?.x ?? 0) as number;
      const baseY = (toolNode?.position?.y ?? 0) as number;
      const w = (toolNode?.measured?.width ?? 320) as number;
      const gap = 140;
      const newId = nanoid();

      addNodes({
        id: newId,
        type: 'upload',
        position: { x: baseX - w - gap, y: baseY },
        data: {
          fileType: 'image',
          updatedAt: new Date().toISOString(),
        },
        origin: [0, 0.5],
        selected: true,
      } as any);

      try {
        addEdges({
          id: nanoid(),
          source: newId,
          target: nodeId,
          type: 'animated',
        } as any);
      } catch {}

      // Select only the new upload node (so user can immediately upload)
      setTimeout(() => {
        try {
          setNodes((prev: any[]) =>
            prev.map((n: any) => ({ ...n, selected: String(n.id) === String(newId) }))
          );
        } catch {}
      }, 0);
    } catch {}
  };

  return (
    <div className="flex flex-col gap-4 w-full h-full">
      <div className="flex items-center justify-between px-1">
        <div className="text-sm font-medium">{t('tool.relight.title')}</div>
      </div>

      <div className="space-y-4">
        {/* Source image: read from upstream node (no preview needed). */}
        <div className="space-y-2 nodrag">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="w-full gap-2 border border-dashed border-primary/70 text-primary hover:bg-primary/10 hover:text-primary"
            onClick={handleAddUpload}
            disabled={!nodeId || hasImage}
            title={uploadLabel}
          >
            <UploadIcon size={14} />
            {uploadLabel}
          </Button>
        </div>

        <div className="space-y-2">
          <Label>{t('tool.relight.light_direction')}</Label>
          <Select
            value={params.light_direction}
            onValueChange={(v) =>
              onChange({ light_direction: v as RelightParams['light_direction'] })
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left">{t('tool.relight.light_direction.left')}</SelectItem>
              <SelectItem value="right">{t('tool.relight.light_direction.right')}</SelectItem>
              <SelectItem value="top">{t('tool.relight.light_direction.top')}</SelectItem>
              <SelectItem value="back">{t('tool.relight.light_direction.back')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t('tool.relight.light_intensity')}</Label>
          <Select
            value={params.light_intensity}
            onValueChange={(v) =>
              onChange({ light_intensity: v as RelightParams['light_intensity'] })
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mood">{t('tool.relight.light_intensity.mood')}</SelectItem>
              <SelectItem value="bright">{t('tool.relight.light_intensity.bright')}</SelectItem>
              <SelectItem value="medium">{t('tool.relight.light_intensity.medium')}</SelectItem>
              <SelectItem value="strong">{t('tool.relight.light_intensity.strong')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t('tool.relight.light_temperature')}</Label>
          <Select
            value={params.light_temperature}
            onValueChange={(v) =>
              onChange({ light_temperature: v as RelightParams['light_temperature'] })
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="warm">{t('tool.relight.light_temperature.warm')}</SelectItem>
              <SelectItem value="cool">{t('tool.relight.light_temperature.cool')}</SelectItem>
              <SelectItem value="neutral">{t('tool.relight.light_temperature.neutral')}</SelectItem>
              <SelectItem value="sunset">{t('tool.relight.light_temperature.sunset')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t('tool.relight.light_type')}</Label>
          <Select
            value={params.light_type}
            onValueChange={(v) => onChange({ light_type: v as RelightParams['light_type'] })}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="softbox">{t('tool.relight.light_type.softbox')}</SelectItem>
              <SelectItem value="sunlight">{t('tool.relight.light_type.sunlight')}</SelectItem>
              <SelectItem value="spotlight">{t('tool.relight.light_type.spotlight')}</SelectItem>
              <SelectItem value="ambient">{t('tool.relight.light_type.ambient')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};


